import React, { useEffect, useState } from 'react';
import './style.less';
import { useAppSelector } from 'shared/store';
import { useSalesfloorRoom } from 'shared/hooks/salesfloor-room';
import { VideoRoomManager } from 'shared/hooks/video-room';
import { joinSalesfloorRoom } from 'shared/api';
import { setListenedDialingParticipantIds } from 'shared/store/salesfloor-slice';
import { useDispatch } from 'react-redux';
import { usePrevious } from 'shared/hooks';
import { OutboundSessionStates } from '../Outbound/OutboundSession';
import { makeLog } from 'shared/utils/log';
import { useVideoRoomContext } from 'shared/contexts/video-room';
import { DialSession } from 'types/outbound';

const log = makeLog('OutboundLiveListenForwarder');

interface OutboundLiveListenForwarderSessionProps {
  roomId: string;
  videoRoomUrl: string;
  dialSession: DialSession;
  videoRoomManager: VideoRoomManager;
}

// This component is needed so that we can use the useSalesfloorRoom hook without changes
export const OutboundLiveListenForwarderSession = (
  props: OutboundLiveListenForwarderSessionProps,
) => {
  // This handles stream mixing with outbound
  useSalesfloorRoom(props.roomId, props.videoRoomManager);
  const dispatch = useDispatch();
  const { join, leave, acquireStream } = props.videoRoomManager;

  // Join on mount
  useEffect(() => {
    const joinRoom = async () => {
      if (props.roomId) {
        const {
          data: { token },
        } = await joinSalesfloorRoom(props.roomId);

        await join(props.videoRoomUrl, token, 'Unknown');

        acquireStream(true, false);

        dispatch(
          setListenedDialingParticipantIds({
            id: `user_${props.dialSession.userId}`,
            listens: true,
          }),
        );
      }
    };
    if (props.roomId) {
      log.info(`Joining Salesfloor room ${props.roomId}`);
      joinRoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leave on unmount (Salesfloor room is left in the useSalesfloorRoom hook)
  useEffect(() => {
    return () => {
      log.info('Leaving video room before unmounting');
      try {
        void leave();
      } catch (e) {
        log.warn('Error leaving video room', e);
      }
    };
  }, [leave]);

  return <></>;
};

export const OutboundLiveListenForwarder = () => {
  const outboundStatus = useAppSelector(s => s.outbound.status);
  const prevOutboundStatus = usePrevious(outboundStatus);
  const dialSession = useAppSelector(s => s.outbound.dialSession);
  const videoRoomManager = useVideoRoomContext();
  const [liveListenRoomId, setLiveListenRoomId] = useState<string | null>(null);
  const [videoRoomUrl, setVideoRoomURL] = useState<string | null>(null);

  // react to the outbount session state changes
  useEffect(() => {
    if (
      outboundStatus === OutboundSessionStates.connected &&
      prevOutboundStatus !== OutboundSessionStates.connected &&
      dialSession?.liveListenRoom
    ) {
      if (liveListenRoomId !== dialSession.liveListenRoom.id) {
        log.debug(`Setting Live Listen Room ID to ${dialSession.liveListenRoom.id}`);
        setLiveListenRoomId(dialSession.liveListenRoom.id);
        setVideoRoomURL(dialSession.liveListenRoom.videoRoom.dailyVideoRoom?.url || '');
      }
    }

    if (
      outboundStatus === OutboundSessionStates.closed &&
      prevOutboundStatus !== OutboundSessionStates.closed
    ) {
      setLiveListenRoomId(null);
      setVideoRoomURL(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboundStatus]);

  if (liveListenRoomId && videoRoomUrl && dialSession) {
    log.info('Forwarding outbound session to LiveListen');
    return (
      <OutboundLiveListenForwarderSession
        roomId={liveListenRoomId}
        videoRoomUrl={videoRoomUrl}
        dialSession={dialSession}
        videoRoomManager={videoRoomManager}
      />
    );
  } else {
    log.debug(
      'Not forwarding the outbound session to LiveListen because conditions not met',
      {
        liveListenRoomId,
        videoRoomUrl,
        dialSession,
      },
    );
    return <></>;
  }
};
